import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/ProjectProgressDashboard.css";
import axios from "axios";
import { Base_URL } from "../url";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────
// Reusable Multi-Select Dropdown Component
// ─────────────────────────────────────────────
const MultiSelectDropdown = ({
  label,
  options = [],
  selected = [],
  onChange,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((opt) =>
    opt?.toLowerCase().includes(search.toLowerCase()),
  );

  const isAllSelected =
    filtered.length > 0 && filtered.every((opt) => selected.includes(opt));

  const toggleOption = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const toggleAll = () => {
    if (isAllSelected) {
      onChange(selected.filter((v) => !filtered.includes(v)));
    } else {
      onChange([...new Set([...selected, ...filtered])]);
    }
  };

  const displayText =
    selected.length === 0
      ? placeholder || `Select ${label}`
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  return (
    <div className="msd-wrapper" ref={ref}>
      <div
        className={`msd-trigger ${open ? "msd-trigger--open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span
          className={`msd-trigger-text ${selected.length === 0 ? "msd-trigger-text--placeholder" : ""}`}
        >
          {displayText}
        </span>
        <span className="msd-arrow">{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="msd-panel">
          <div className="msd-search-wrap">
            <input
              className="msd-search"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          {filtered.length > 0 && (
            <label className="msd-option msd-option--all">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleAll}
              />
              <span>Select All</span>
            </label>
          )}

          <div className="msd-options-list">
            {filtered.length === 0 ? (
              <div className="msd-no-results">No results</div>
            ) : (
              filtered.map((opt, i) => (
                <label key={i} className="msd-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleOption(opt)}
                  />
                  <span>{opt}</span>
                </label>
              ))
            )}
          </div>

          {selected.length > 0 && (
            <div className="msd-footer">
              {selected.length} selected
              <button
                className="msd-clear-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ProjectProgressDashboard
// ─────────────────────────────────────────────────────────────────────────────
const ProjectProgressDashboard = () => {
  const { user } = useAuth();
  const orgId = user?.organisationId || "";
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    type: "All",
    client: [],
    project: [],
    resource: [],
    submittedBy: [],
    fromDate: "",
    toDate: "",
    dueDate: "",
    status: "All",
    dueStatus: [],
    changedFromDate: "",
    changedToDate: "",
    projectManager: [],
  });

  // ── Raw full dataset from API — never mutated after fetch ──
  const [allRawData, setAllRawData] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  const [dashboardData, setDashboardData] = useState({
    client: [],
    project: [],
    priority: [],
    resource: [],
    projectManager: [],
    statuses: [],
  });

  // All tabs open by default
  const [expanded, setExpanded] = useState({
    client: true,
    project: true,
    priority: true,
    resource: true,
    projectManager: true,
  });

  const [dropdownData, setDropdownData] = useState({
    clients: [],
    projects: [],
    types: [],
    resources: [],
    submittedBy: [],
    dueStatus: [],
    statuses: [],
    projectManagers: [],
  });

  const handleChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ─────────────────────────────────────────────────────────
  // buildPivot — unchanged from your original
  // ─────────────────────────────────────────────────────────
  const buildPivot = (data, keyField, nameField, allStatuses) => {
    const map = {};
    data.forEach((item) => {
      const key = item[keyField] || "Unassigned";
      const name = item[nameField] || "Unassigned";
      const status = item.status || "Unknown";

      if (!map[key]) {
        map[key] = { name, counts: {}, total: 0 };
        allStatuses.forEach((s) => {
          map[key].counts[s] = 0;
        });
      }
      map[key].counts[status]++;
      map[key].total++;
    });
    return Object.values(map);
  };

  // ─────────────────────────────────────────────────────────
  // applyFiltersToRaw
  // ─────────────────────────────────────────────────────────
  const applyFiltersToRaw = (rawData, f) => {
    return rawData.filter((item) => {
      // Single-select: type
      if (f.type && f.type !== "All") {
        if ((item.type || "").toLowerCase() !== f.type.toLowerCase())
          return false;
      }

      // Single-select: status
      if (f.status && f.status !== "All") {
        if (
          (item.status || "").trim().toLowerCase() !==
          f.status.trim().toLowerCase()
        )
          return false;
      }

      // Multi-select: client
      if (f.client && f.client.length > 0) {
        if (!f.client.includes(item.clientShortName)) return false;
      }

      // Multi-select: project
      if (f.project && f.project.length > 0) {
        if (!f.project.includes(item.prTitle)) return false;
      }

      // Multi-select: resource
      if (f.resource && f.resource.length > 0) {
        if (!f.resource.includes(item.assignedToName)) return false;
      }

      // Multi-select: submittedBy
      if (f.submittedBy && f.submittedBy.length > 0) {
        if (!f.submittedBy.includes(item.submittedBy)) return false;
      }

      // Multi-select: dueStatus
      if (f.dueStatus && f.dueStatus.length > 0) {
        if (!f.dueStatus.includes(item.dueStatus)) return false;
      }

      // Multi-select: projectManager
      if (f.projectManager && f.projectManager.length > 0) {
        if (!f.projectManager.includes(item.projectManagerName)) return false;
      }

      return true;
    });
  };

  const computeAndSetDashboard = (rawData, f) => {
    const filtered = applyFiltersToRaw(rawData, f);

    if (!filtered.length) {
      setDashboardData({
        client: [],
        project: [],
        priority: [],
        resource: [],
        projectManager: [],
        statuses: [],
      });
      return;
    }

    const allStatuses = [
      ...new Set(filtered.map((item) => item.status || "Unknown")),
    ];

    setDashboardData({
      client: buildPivot(
        filtered,
        "clientShortName",
        "clientShortName",
        allStatuses,
      ),
      project: buildPivot(filtered, "prTitle", "prTitle", allStatuses),
      priority: buildPivot(filtered, "priority", "priority", allStatuses),
      resource: buildPivot(
        filtered,
        "assignedToName",
        "assignedToName",
        allStatuses,
      ),
      projectManager: buildPivot(
        filtered,
        "projectManagerName",
        "projectManagerName",
        allStatuses,
      ),
      statuses: allStatuses,
    });
  };

  // ─────────────────────────────────────────────────────────
  // handleApply
  // Fetches ALL data from backend (only orgId + single-value
  // params that the backend definitely handles), then filters
  // multi-select values entirely on the frontend.
  // ─────────────────────────────────────────────────────────
  const handleApply = async (overrideFilters) => {
    setIsLoading(true);
    try {
      const f = overrideFilters || filters;

      // Send ONLY the params the backend reliably handles
      const backendParams = { orgId: user?.organisationId };
      if (f.type && f.type !== "All") backendParams.type = f.type;
      if (f.status && f.status !== "All") backendParams.status = f.status;

      console.log("🚀 Backend params:", backendParams);

      const res = await axios.get(`${Base_URL}/ProjectProgress/list-v2`, {
        params: backendParams,
      });

      const apiData = res.data?.data || res.data?.Data || res.data;
      const rawData = Array.isArray(apiData) ? apiData : [];

      console.log("🚀 Raw data from API:", rawData);

      console.log(`✅ Raw data from API: ${rawData.length} records`);

      // Store full raw data for re-use on subsequent Apply clicks
      setAllRawData(rawData);

      // Apply multi-select filters on frontend
      computeAndSetDashboard(rawData, f);
    } catch (err) {
      console.error("❌ APPLY ERROR:", err);
    } finally {
      // Add a small delay to ensure loader is visible
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  };

  // When filters change without a new API call needed
  // (e.g. user changes multi-select then clicks Apply),
  // recompute from the already-fetched raw data.
  const handleApplyFrontendOnly = () => {
    if (allRawData.length > 0) {
      setIsLoading(true);
      // Add a small delay to show loader
      setTimeout(() => {
        computeAndSetDashboard(allRawData, filters);
        setIsLoading(false);
      }, 300);
    } else {
      // No raw data yet — do a full fetch
      handleApply(filters);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      let dataToUse = dashboardData;

      const isEmpty =
        !dashboardData.client.length &&
        !dashboardData.project.length &&
        !dashboardData.priority.length &&
        !dashboardData.resource.length &&
        !dashboardData.projectManager.length;

      if (isEmpty) {
        setIsLoading(true);
        const res = await axios.get(`${Base_URL}/ProjectProgress/list-v2`, {
          params: { orgId },
        });

        const apiData = res.data?.data || res.data?.Data || res.data;
        const rawData = Array.isArray(apiData) ? apiData : [apiData];
        const allStatuses = [
          ...new Set(rawData.map((item) => item.status || "Unknown")),
        ];

        dataToUse = {
          client: buildPivot(
            rawData,
            "clientShortName",
            "clientShortName",
            allStatuses,
          ),
          project: buildPivot(rawData, "prTitle", "prTitle", allStatuses),
          priority: buildPivot(rawData, "Priority", "Priority", allStatuses),
          resource: buildPivot(
            rawData,
            "assignedTo",
            "assignedTo",
            allStatuses,
          ),
          projectManager: buildPivot(
            rawData,
            "projectManagerName",
            "projectManagerName",
            allStatuses,
          ),
          statuses: allStatuses,
        };
        setIsLoading(false);
      }

      const wb = XLSX.utils.book_new();

      const createSheet = (sheetName, rows) => {
        if (!rows.length) return;
        const formatted = rows.map((row) => {
          let obj = { Name: row.name };
          dataToUse.statuses.forEach((s) => {
            obj[s] = row.counts[s];
          });
          obj["Total"] = row.total;
          return obj;
        });
        const ws = XLSX.utils.json_to_sheet(formatted);
        ws["!cols"] = [
          { wch: 25 },
          ...dataToUse.statuses.map(() => ({ wch: 15 })),
          { wch: 12 },
        ];
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      };

      createSheet("Client", dataToUse.client);
      createSheet("Project", dataToUse.project);
      createSheet("Priority", dataToUse.priority);
      createSheet("Resource", dataToUse.resource);
      createSheet("Project Manager", dataToUse.projectManager);

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      saveAs(blob, "Project_Progress_Report.xlsx");
    } catch (err) {
      console.error("Excel Download Error:", err);
    }
  };

  const handlePivotClick = (type, value, status) => {
    console.log("🔵 PIVOT CLICK:", { type, value, status });
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, val]) => {
      if (Array.isArray(val)) {
        val.forEach((v) => {
          if (v) params.append(key, v);
        });
      } else if (val && val !== "All") {
        params.append(key, val);
      }
    });

    if (type) params.set(type, value);
    if (status) params.set("status", status);

    navigate(`/ProjectProgressList?${params.toString()}`);
  };

  const handleClear = () => {
    const resetFilters = {
      type: "All",
      client: [],
      project: [],
      resource: [],
      submittedBy: [],
      fromDate: "",
      toDate: "",
      dueDate: "",
      status: "All",
      dueStatus: [],
      changedFromDate: "",
      changedToDate: "",
      projectManager: [],
    };

    setFilters(resetFilters);

    // Use already-fetched raw data, just clear all filters
    if (allRawData.length > 0) {
      setIsLoading(true);
      setTimeout(() => {
        computeAndSetDashboard(allRawData, resetFilters);
        setIsLoading(false);
      }, 300);
    } else {
      handleApply(resetFilters);
    }
  };

  const toggleSection = (section) => {
    setExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // In the fetchDropdowns function, update the mapping:
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await axios.get(
          `${Base_URL}/ProjectProgress/dropdowns-v2`,
          {
            params: { orgId: user?.organisationId },
          },
        );

        console.log("DROPDOWN API FULL RESPONSE:", res.data);
        console.log(
          "DROPDOWN API DATA STRUCTURE:",
          JSON.stringify(res.data, null, 2),
        );

        const data = res?.data?.data || res?.data?.Data || res?.data || {};

        // Log each potential field to see what's available
        console.log("Project Managers from API:", {
          direct: data.projectManagers,
          alt1: data.ProjectManagers,
          alt2: data.project_manager,
          alt3: data.Project_Manager,
          alt4: data.managers,
          alt5: data.Managers,
          allKeys: Object.keys(data),
        });

        setDropdownData({
          clients: data.clients || data.Clients || [],
          projects: data.projects || data.Projects || [],
          resources: data.resources || data.Resources || [],
          submittedBy: data.submittedBy || data.SubmittedBy || [],
          types: data.types || data.Types || [],
          dueStatus: data.dueStatuses || data.DueStatuses || [],
          statuses: data.statuses || data.Statuses || [],
          // Try multiple possible field names for project managers
          projectManagers:
            data.projectManagers ||
            data.ProjectManagers ||
            data.project_manager ||
            data.Project_Manager ||
            data.managers ||
            data.Managers ||
            [],
        });
      } catch (err) {
        console.error("Dropdown error:", err);
      }
    };

    if (user?.organisationId) {
      fetchDropdowns();
      handleApply();
    }
  }, [user]);

  // Loader Component
  const Loader = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <div
        style={{
          width: "50px",
          height: "50px",
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #687c9a",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }}
      ></div>
      <div style={{ color: "#687c9a", fontSize: "16px", fontWeight: "500" }}>
        Loading dashboard data...
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  return (
    <div className="ppd-container">
      <div className="ppd-header">
        <h2 className="ppd-title">Project Progress Dashboard</h2>

        <button className="download-btn" onClick={handleDownloadExcel}>
          <span>⬇ Download Excel</span>
        </button>
      </div>

      <div className="ppd-filter-box">
        {/* Org ID */}
        <div className="ppd-field">
          <label>Organisation ID</label>
          <input value={user.organisationId || ""} disabled />
        </div>

        {/* Type — unchanged single select */}
        <div className="ppd-field">
          <label>Type</label>
          <select
            value={filters.type}
            onChange={(e) => handleChange("type", e.target.value)}
          >
            <option value="All">All</option>
            {dropdownData.types.map((t, i) => (
              <option key={i} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {/* Client — MULTI-SELECT */}
        <div className="ppd-field">
          <label>Client</label>
          <MultiSelectDropdown
            label="Client"
            options={dropdownData.clients}
            selected={filters.client}
            onChange={(val) => handleChange("client", val)}
            placeholder="Select Client"
          />
        </div>

        {/* Project — MULTI-SELECT */}
        <div className="ppd-field">
          <label>Project</label>
          <MultiSelectDropdown
            label="Project"
            options={dropdownData.projects}
            selected={filters.project}
            onChange={(val) => handleChange("project", val)}
            placeholder="Select Project"
          />
        </div>

        {/* Resource — MULTI-SELECT */}
        <div className="ppd-field">
          <label>Assigned To</label>
          <MultiSelectDropdown
            label="Resource"
            options={dropdownData.resources}
            selected={filters.resource}
            onChange={(val) => handleChange("resource", val)}
            placeholder="Select Resource"
          />
        </div>

        {/* Submitted By — MULTI-SELECT */}
        <div className="ppd-field">
          <label>Submitted By</label>
          <MultiSelectDropdown
            label="Submitted By"
            options={dropdownData.submittedBy}
            selected={filters.submittedBy}
            onChange={(val) => handleChange("submittedBy", val)}
            placeholder="Select Submitted By"
          />
        </div>

        {/* Project Manager — MULTI-SELECT */}
        <div className="ppd-field">
          <label>Project Manager</label>
          <MultiSelectDropdown
            label="Project Manager"
            options={dropdownData.projectManagers}
            selected={filters.projectManager}
            onChange={(val) => handleChange("projectManager", val)}
            placeholder="Select Project Manager"
          />
        </div>

        <hr />

        {/* Due Status — MULTI-SELECT */}
        <div className="ppd-field">
          <label>Due Status</label>
          <MultiSelectDropdown
            label="Due Status"
            options={dropdownData.dueStatus}
            selected={filters.dueStatus}
            onChange={(val) => handleChange("dueStatus", val)}
            placeholder="All"
          />
        </div>

        {/* Status — unchanged single select */}
        <div className="ppd-field">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => handleChange("status", e.target.value)}
          >
            <option value="All">All</option>
            {dropdownData.statuses.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <br />
        <br />

        <div className="ppd-buttons">
          {/* Apply now filters frontend from cached raw data */}
          <button className="apply-btn" onClick={handleApplyFrontendOnly}>
            Apply
          </button>
          <button className="clear-btn" onClick={handleClear}>
            Clear
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <Loader />
      ) : (
        <div className="ppd-list-sections">
          {/* CLIENT */}
          <div className="ppd-list-item">
            <div
              className="ppd-list-header"
              onClick={() => toggleSection("client")}
            >
              <span>Client → Status</span>
              <span>{expanded.client ? "▲" : "▼"}</span>
            </div>

            {expanded.client && (
              <div className="ppd-list-content">
                {dashboardData.client.length ? (
                  <table className="ppd-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        {dashboardData.statuses.map((s, i) => (
                          <th key={i}>{s}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.client.map((row, i) => (
                        <tr key={i}>
                          <td>{row.name}</td>
                          {dashboardData.statuses.map((s, j) => (
                            <td key={j}>
                              <span
                                style={{ color: "#007bff", cursor: "pointer" }}
                                onClick={() =>
                                  handlePivotClick("client", row.name, s)
                                }
                              >
                                {row.counts[s]}
                              </span>
                            </td>
                          ))}
                          <td>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No Data</p>
                )}
              </div>
            )}
          </div>

          {/* PROJECT */}
          <div className="ppd-list-item">
            <div
              className="ppd-list-header"
              onClick={() => toggleSection("project")}
            >
              <span>Project → Status</span>
              <span>{expanded.project ? "▲" : "▼"}</span>
            </div>

            {expanded.project && (
              <div className="ppd-list-content">
                {dashboardData.project.length ? (
                  <table className="ppd-table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        {dashboardData.statuses.map((s, i) => (
                          <th key={i}>{s}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.project.map((row, i) => (
                        <tr key={i}>
                          <td>{row.name}</td>
                          {dashboardData.statuses.map((s, j) => (
                            <td key={j}>
                              <span
                                style={{ color: "#007bff", cursor: "pointer" }}
                                onClick={() =>
                                  handlePivotClick("project", row.name, s)
                                }
                              >
                                {row.counts[s]}
                              </span>
                            </td>
                          ))}
                          <td>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No Data</p>
                )}
              </div>
            )}
          </div>

          {/* PRIORITY */}
          <div className="ppd-list-item">
            <div
              className="ppd-list-header"
              onClick={() => toggleSection("priority")}
            >
              <span>Priority → Status</span>
              <span>{expanded.priority ? "▲" : "▼"}</span>
            </div>

            {expanded.priority && (
              <div className="ppd-list-content">
                {dashboardData.priority.length ? (
                  <table className="ppd-table">
                    <thead>
                      <tr>
                        <th>Priority</th>
                        {dashboardData.statuses.map((s, i) => (
                          <th key={i}>{s}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.priority.map((row, i) => (
                        <tr key={i}>
                          <td>{row.name}</td>
                          {dashboardData.statuses.map((s, j) => (
                            <td key={j}>
                              <span
                                style={{ color: "#007bff", cursor: "pointer" }}
                                onClick={() =>
                                  handlePivotClick("priority", row.name, s)
                                }
                              >
                                {row.counts[s]}
                              </span>
                            </td>
                          ))}
                          <td>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No Data</p>
                )}
              </div>
            )}
          </div>

          {/* RESOURCE */}
          <div className="ppd-list-item">
            <div
              className="ppd-list-header"
              onClick={() => toggleSection("resource")}
            >
              <span>Resource → Status</span>
              <span>{expanded.resource ? "▲" : "▼"}</span>
            </div>

            {expanded.resource && (
              <div className="ppd-list-content">
                {dashboardData.resource.length ? (
                  <table className="ppd-table">
                    <thead>
                      <tr>
                        <th>Resource</th>
                        {dashboardData.statuses.map((s, i) => (
                          <th key={i}>{s}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.resource.map((row, i) => (
                        <tr key={i}>
                          <td>{row.name || "Unassigned"}</td>
                          {dashboardData.statuses.map((s, j) => (
                            <td key={j}>
                              <span
                                style={{ color: "#007bff", cursor: "pointer" }}
                                onClick={() =>
                                  handlePivotClick("resource", row.name, s)
                                }
                              >
                                {row.counts[s]}
                              </span>
                            </td>
                          ))}
                          <td>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No Data</p>
                )}
              </div>
            )}
          </div>

          {/* PROJECT MANAGER */}
          <div className="ppd-list-item">
            <div
              className="ppd-list-header"
              onClick={() => toggleSection("projectManager")}
            >
              <span>Project Manager → Status</span>
              <span>{expanded.projectManager ? "▲" : "▼"}</span>
            </div>

            {expanded.projectManager && (
              <div className="ppd-list-content">
                {dashboardData.projectManager.length ? (
                  <table className="ppd-table">
                    <thead>
                      <tr>
                        <th>Project Manager</th>
                        {dashboardData.statuses.map((s, i) => (
                          <th key={i}>{s}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.projectManager.map((row, i) => (
                        <tr key={i}>
                          <td>{row.name || "Unassigned"}</td>
                          {dashboardData.statuses.map((s, j) => (
                            <td key={j}>
                              <span
                                style={{ color: "#007bff", cursor: "pointer" }}
                                onClick={() =>
                                  handlePivotClick(
                                    "projectManager",
                                    row.name,
                                    s,
                                  )
                                }
                              >
                                {row.counts[s]}
                              </span>
                            </td>
                          ))}
                          <td>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No Data</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectProgressDashboard;

// import React, { useState, useRef, useEffect } from "react";
// import { useAuth } from "../Context/AuthContext";
// import "../Styling/ProjectProgressDashboard.css";
// import axios from "axios";
// import { Base_URL } from "../url";
// import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";
// import { useNavigate } from "react-router-dom";

// // ─────────────────────────────────────────────
// // Reusable Multi-Select Dropdown Component
// // ─────────────────────────────────────────────
// const MultiSelectDropdown = ({
//   label,
//   options = [],
//   selected = [],
//   onChange,
//   placeholder,
// }) => {
//   const [open, setOpen] = useState(false);
//   const [search, setSearch] = useState("");
//   const ref = useRef(null);

//   useEffect(() => {
//     const handler = (e) => {
//       if (ref.current && !ref.current.contains(e.target)) setOpen(false);
//     };
//     document.addEventListener("mousedown", handler);
//     return () => document.removeEventListener("mousedown", handler);
//   }, []);

//   const filtered = options.filter((opt) =>
//     opt?.toLowerCase().includes(search.toLowerCase()),
//   );

//   const isAllSelected =
//     filtered.length > 0 && filtered.every((opt) => selected.includes(opt));

//   const toggleOption = (val) => {
//     if (selected.includes(val)) {
//       onChange(selected.filter((v) => v !== val));
//     } else {
//       onChange([...selected, val]);
//     }
//   };

//   const toggleAll = () => {
//     if (isAllSelected) {
//       onChange(selected.filter((v) => !filtered.includes(v)));
//     } else {
//       onChange([...new Set([...selected, ...filtered])]);
//     }
//   };

//   const displayText =
//     selected.length === 0
//       ? placeholder || `Select ${label}`
//       : selected.length === 1
//         ? selected[0]
//         : `${selected.length} selected`;

//   return (
//     <div className="msd-wrapper" ref={ref}>
//       <div
//         className={`msd-trigger ${open ? "msd-trigger--open" : ""}`}
//         onClick={() => setOpen((prev) => !prev)}
//       >
//         <span
//           className={`msd-trigger-text ${selected.length === 0 ? "msd-trigger-text--placeholder" : ""}`}
//         >
//           {displayText}
//         </span>
//         <span className="msd-arrow">{open ? "▲" : "▼"}</span>
//       </div>

//       {open && (
//         <div className="msd-panel">
//           <div className="msd-search-wrap">
//             <input
//               className="msd-search"
//               placeholder="Search..."
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//               onClick={(e) => e.stopPropagation()}
//               autoFocus
//             />
//           </div>

//           {filtered.length > 0 && (
//             <label className="msd-option msd-option--all">
//               <input
//                 type="checkbox"
//                 checked={isAllSelected}
//                 onChange={toggleAll}
//               />
//               <span>Select All</span>
//             </label>
//           )}

//           <div className="msd-options-list">
//             {filtered.length === 0 ? (
//               <div className="msd-no-results">No results</div>
//             ) : (
//               filtered.map((opt, i) => (
//                 <label key={i} className="msd-option">
//                   <input
//                     type="checkbox"
//                     checked={selected.includes(opt)}
//                     onChange={() => toggleOption(opt)}
//                   />
//                   <span>{opt}</span>
//                 </label>
//               ))
//             )}
//           </div>

//           {selected.length > 0 && (
//             <div className="msd-footer">
//               {selected.length} selected
//               <button
//                 className="msd-clear-btn"
//                 onClick={(e) => {
//                   e.stopPropagation();
//                   onChange([]);
//                 }}
//               >
//                 Clear
//               </button>
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // ProjectProgressDashboard
// // ─────────────────────────────────────────────────────────────────────────────
// const ProjectProgressDashboard = () => {
//   const { user } = useAuth();
//   const orgId = user?.organisationId || "";
//   const navigate = useNavigate();

//   const [filters, setFilters] = useState({
//     type: "All",
//     client: [],
//     project: [],
//     resource: [],
//     submittedBy: [],
//     fromDate: "",
//     toDate: "",
//     dueDate: "",
//     status: "All",
//     dueStatus: [],
//     changedFromDate: "",
//     changedToDate: "",
//   });

//   // ── Raw full dataset from API — never mutated after fetch ──
//   const [allRawData, setAllRawData] = useState([]);

//   const [dashboardData, setDashboardData] = useState({
//     client: [],
//     project: [],
//     priority: [],
//     resource: [],
//     statuses: [],
//   });

//   const [expanded, setExpanded] = useState({
//     client: false,
//     project: false,
//     priority: false,
//     resource: false,
//   });

//   const [dropdownData, setDropdownData] = useState({
//     clients: [],
//     projects: [],
//     types: [],
//     resources: [],
//     submittedBy: [],
//     dueStatus: [],
//     statuses: [],
//   });

//   const handleChange = (field, value) => {
//     setFilters((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   // ─────────────────────────────────────────────────────────
//   // buildPivot — unchanged from your original
//   // ─────────────────────────────────────────────────────────
//   const buildPivot = (data, keyField, nameField, allStatuses) => {
//     const map = {};
//     data.forEach((item) => {
//       const key = item[keyField] || "Unassigned";
//       const name = item[nameField] || "Unassigned";
//       const status = item.status || "Unknown";

//       if (!map[key]) {
//         map[key] = { name, counts: {}, total: 0 };
//         allStatuses.forEach((s) => {
//           map[key].counts[s] = 0;
//         });
//       }
//       map[key].counts[status]++;
//       map[key].total++;
//     });
//     return Object.values(map);
//   };

//   // ─────────────────────────────────────────────────────────
//   // applyFiltersToRaw
//   // ─────────────────────────────────────────────────────────
//   const applyFiltersToRaw = (rawData, f) => {
//     return rawData.filter((item) => {
//       // Single-select: type
//       if (f.type && f.type !== "All") {
//         if ((item.type || "").toLowerCase() !== f.type.toLowerCase())
//           return false;
//       }

//       // Single-select: status
//       if (f.status && f.status !== "All") {
//         if (
//           (item.status || "").trim().toLowerCase() !==
//           f.status.trim().toLowerCase()
//         )
//           return false;
//       }

//       // Multi-select: client
//       if (f.client && f.client.length > 0) {
//         if (!f.client.includes(item.clientShortName)) return false;
//       }

//       // Multi-select: project
//       if (f.project && f.project.length > 0) {
//         if (!f.project.includes(item.prTitle)) return false;
//       }

//       // Multi-select: resource
//       if (f.resource && f.resource.length > 0) {
//         if (!f.resource.includes(item.assignedToName)) return false;
//       }

//       // Multi-select: submittedBy
//       if (f.submittedBy && f.submittedBy.length > 0) {
//         if (!f.submittedBy.includes(item.submittedBy)) return false;
//       }

//       // Multi-select: dueStatus
//       if (f.dueStatus && f.dueStatus.length > 0) {
//         if (!f.dueStatus.includes(item.dueStatus)) return false;
//       }

//       return true;
//     });
//   };

//   const computeAndSetDashboard = (rawData, f) => {
//     const filtered = applyFiltersToRaw(rawData, f);

//     if (!filtered.length) {
//       setDashboardData({
//         client: [],
//         project: [],
//         priority: [],
//         resource: [],
//         statuses: [],
//       });
//       return;
//     }

//     const allStatuses = [
//       ...new Set(filtered.map((item) => item.status || "Unknown")),
//     ];

//     setDashboardData({
//       client: buildPivot(
//         filtered,
//         "clientShortName",
//         "clientShortName",
//         allStatuses,
//       ),
//       project: buildPivot(filtered, "prTitle", "prTitle", allStatuses),
//       priority: buildPivot(filtered, "priority", "priority", allStatuses),
//       resource: buildPivot(
//         filtered,
//         "assignedToName",
//         "assignedToName",
//         allStatuses,
//       ),
//       statuses: allStatuses,
//     });
//   };

//   // ─────────────────────────────────────────────────────────
//   // handleApply
//   // Fetches ALL data from backend (only orgId + single-value
//   // params that the backend definitely handles), then filters
//   // multi-select values entirely on the frontend.
//   // ─────────────────────────────────────────────────────────
//   const handleApply = async (overrideFilters) => {
//     try {
//       const f = overrideFilters || filters;

//       // Send ONLY the params the backend reliably handles
//       const backendParams = { orgId: user?.organisationId };
//       if (f.type && f.type !== "All") backendParams.type = f.type;
//       if (f.status && f.status !== "All") backendParams.status = f.status;

//       console.log("🚀 Backend params:", backendParams);

//       const res = await axios.get(`${Base_URL}/ProjectProgress/list-v2`, {
//         params: backendParams,
//       });

//       const apiData = res.data?.data || res.data?.Data || res.data;
//       const rawData = Array.isArray(apiData) ? apiData : [];

//       console.log("🚀 Raw data from API:", rawData);

//       console.log(`✅ Raw data from API: ${rawData.length} records`);

//       // Store full raw data for re-use on subsequent Apply clicks
//       setAllRawData(rawData);

//       // Apply multi-select filters on frontend
//       computeAndSetDashboard(rawData, f);
//     } catch (err) {
//       console.error("❌ APPLY ERROR:", err);
//     }
//   };

//   // When filters change without a new API call needed
//   // (e.g. user changes multi-select then clicks Apply),
//   // recompute from the already-fetched raw data.
//   const handleApplyFrontendOnly = () => {
//     if (allRawData.length > 0) {
//       computeAndSetDashboard(allRawData, filters);
//     } else {
//       // No raw data yet — do a full fetch
//       handleApply(filters);
//     }
//   };

//   const handleDownloadExcel = async () => {
//     try {
//       let dataToUse = dashboardData;

//       const isEmpty =
//         !dashboardData.client.length &&
//         !dashboardData.project.length &&
//         !dashboardData.priority.length &&
//         !dashboardData.resource.length;

//       if (isEmpty) {
//         const res = await axios.get(`${Base_URL}/ProjectProgress/list-v2`, {
//           params: { orgId },
//         });

//         const apiData = res.data?.data || res.data?.Data || res.data;
//         const rawData = Array.isArray(apiData) ? apiData : [apiData];
//         const allStatuses = [
//           ...new Set(rawData.map((item) => item.status || "Unknown")),
//         ];

//         dataToUse = {
//           client: buildPivot(
//             rawData,
//             "clientShortName",
//             "clientShortName",
//             allStatuses,
//           ),
//           project: buildPivot(rawData, "prTitle", "prTitle", allStatuses),
//           priority: buildPivot(rawData, "Priority", "Priority", allStatuses),
//           resource: buildPivot(
//             rawData,
//             "assignedTo",
//             "assignedTo",
//             allStatuses,
//           ),
//           statuses: allStatuses,
//         };
//       }

//       const wb = XLSX.utils.book_new();

//       const createSheet = (sheetName, rows) => {
//         if (!rows.length) return;
//         const formatted = rows.map((row) => {
//           let obj = { Name: row.name };
//           dataToUse.statuses.forEach((s) => {
//             obj[s] = row.counts[s];
//           });
//           obj["Total"] = row.total;
//           return obj;
//         });
//         const ws = XLSX.utils.json_to_sheet(formatted);
//         ws["!cols"] = [
//           { wch: 25 },
//           ...dataToUse.statuses.map(() => ({ wch: 15 })),
//           { wch: 12 },
//         ];
//         XLSX.utils.book_append_sheet(wb, ws, sheetName);
//       };

//       createSheet("Client", dataToUse.client);
//       createSheet("Project", dataToUse.project);
//       createSheet("Priority", dataToUse.priority);
//       createSheet("Resource", dataToUse.resource);

//       const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
//       const blob = new Blob([excelBuffer], {
//         type: "application/octet-stream",
//       });
//       saveAs(blob, "Project_Progress_Report.xlsx");
//     } catch (err) {
//       console.error("Excel Download Error:", err);
//     }
//   };

//   const handlePivotClick = (type, value, status) => {
//     console.log("🔵 PIVOT CLICK:", { type, value, status });
//     const params = new URLSearchParams();

//     Object.entries(filters).forEach(([key, val]) => {
//       if (Array.isArray(val)) {
//         val.forEach((v) => {
//           if (v) params.append(key, v);
//         });
//       } else if (val && val !== "All") {
//         params.append(key, val);
//       }
//     });

//     if (type) params.set(type, value);
//     if (status) params.set("status", status);

//     navigate(`/ProjectProgressList?${params.toString()}`);
//   };

//   const handleClear = () => {
//     const resetFilters = {
//       type: "All",
//       client: [],
//       project: [],
//       resource: [],
//       submittedBy: [],
//       fromDate: "",
//       toDate: "",
//       dueDate: "",
//       status: "All",
//       dueStatus: [],
//       changedFromDate: "",
//       changedToDate: "",
//     };

//     setFilters(resetFilters);

//     // Use already-fetched raw data, just clear all filters
//     if (allRawData.length > 0) {
//       computeAndSetDashboard(allRawData, resetFilters);
//     } else {
//       handleApply(resetFilters);
//     }
//   };

//   const toggleSection = (section) => {
//     setExpanded((prev) => ({
//       ...prev,
//       [section]: !prev[section],
//     }));
//   };

//   useEffect(() => {
//     const fetchDropdowns = async () => {
//       try {
//         const res = await axios.get(
//           `${Base_URL}/ProjectProgress/dropdowns-v2`,
//           {
//             params: { orgId: user?.organisationId },
//           },
//         );

//         console.log("DROPDOWN API:", res.data);

//         const data = res?.data?.data || res?.data?.Data || res?.data || {};
//         setDropdownData({
//           clients: data.clients || data.Clients || [],
//           projects: data.projects || data.Projects || [],
//           resources: data.resources || data.Resources || [],
//           submittedBy: data.submittedBy || data.SubmittedBy || [],
//           types: data.types || data.Types || [],
//           dueStatus: data.dueStatuses || data.DueStatuses || [],
//           statuses: data.statuses || data.Statuses || [],
//         });
//       } catch (err) {
//         console.log("Dropdown error:", err);
//       }
//     };

//     if (user?.organisationId) {
//       fetchDropdowns();
//       handleApply();
//     }
//   }, [user]);

//   return (
//     <div className="ppd-container">
//       <div className="ppd-header">
//         <h2 className="ppd-title">Project Progress Dashboard</h2>

//         <button className="download-btn" onClick={handleDownloadExcel}>
//           <span>⬇ Download Excel</span>
//         </button>
//       </div>

//       <div className="ppd-filter-box">
//         {/* Org ID */}
//         <div className="ppd-field">
//           <label>Organisation ID</label>
//           <input value={user.organisationId || ""} disabled />
//         </div>

//         {/* Type — unchanged single select */}
//         <div className="ppd-field">
//           <label>Type</label>
//           <select
//             value={filters.type}
//             onChange={(e) => handleChange("type", e.target.value)}
//           >
//             <option value="All">All</option>
//             {dropdownData.types.map((t, i) => (
//               <option key={i} value={t}>
//                 {t}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Client — MULTI-SELECT */}
//         <div className="ppd-field">
//           <label>Client</label>
//           <MultiSelectDropdown
//             label="Client"
//             options={dropdownData.clients}
//             selected={filters.client}
//             onChange={(val) => handleChange("client", val)}
//             placeholder="Select Client"
//           />
//         </div>

//         {/* Project — MULTI-SELECT */}
//         <div className="ppd-field">
//           <label>Project</label>
//           <MultiSelectDropdown
//             label="Project"
//             options={dropdownData.projects}
//             selected={filters.project}
//             onChange={(val) => handleChange("project", val)}
//             placeholder="Select Project"
//           />
//         </div>

//         {/* Resource — MULTI-SELECT */}
//         <div className="ppd-field">
//           <label>Assigned To</label>
//           <MultiSelectDropdown
//             label="Resource"
//             options={dropdownData.resources}
//             selected={filters.resource}
//             onChange={(val) => handleChange("resource", val)}
//             placeholder="Select Resource"
//           />
//         </div>

//         {/* Submitted By — MULTI-SELECT */}
//         <div className="ppd-field">
//           <label>Submitted By</label>
//           <MultiSelectDropdown
//             label="Submitted By"
//             options={dropdownData.submittedBy}
//             selected={filters.submittedBy}
//             onChange={(val) => handleChange("submittedBy", val)}
//             placeholder="Select Submitted By"
//           />
//         </div>

//         {/* <div className="ppd-field">
//           <label>Due Date</label>
//           <input
//             type="date"
//             value={filters.dueDate}
//             onChange={(e) => handleChange("dueDate", e.target.value)}
//           />
//         </div> */}

//         <hr />

//         {/* Due Status — MULTI-SELECT */}
//         <div className="ppd-field">
//           <label>Due Status</label>
//           <MultiSelectDropdown
//             label="Due Status"
//             options={dropdownData.dueStatus}
//             selected={filters.dueStatus}
//             onChange={(val) => handleChange("dueStatus", val)}
//             placeholder="All"
//           />
//         </div>

//         {/* Status — unchanged single select */}
//         <div className="ppd-field">
//           <label>Status</label>
//           <select
//             value={filters.status}
//             onChange={(e) => handleChange("status", e.target.value)}
//           >
//             <option value="All">All</option>
//             {dropdownData.statuses.map((s, i) => (
//               <option key={i} value={s}>
//                 {s}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* <div className="ppd-date-row">
//           <div className="ppd-field">
//             <label>Created From Date</label>
//             <input
//               type="date"
//               value={filters.fromDate}
//               onChange={(e) => handleChange("fromDate", e.target.value)}
//             />
//           </div>

//           <div className="ppd-field">
//             <label>Created To Date</label>
//             <input
//               type="date"
//               value={filters.toDate}
//               onChange={(e) => handleChange("toDate", e.target.value)}
//             />
//           </div>
//         </div>

//         {/* ✅ Changed Date Row */}
//         {/* <div className="ppd-date-row">
//           <div className="ppd-field">
//             <label>Changed From Date</label>
//             <input
//               type="date"
//               value={filters.changedFromDate}
//               onChange={(e) => handleChange("changedFromDate", e.target.value)}
//             />
//           </div>

//           <div className="ppd-field">
//             <label>Changed To Date</label>
//             <input
//               type="date"
//               value={filters.changedToDate}
//               onChange={(e) => handleChange("changedToDate", e.target.value)}
//             />
//           </div>
//         </div> */}
//         <br />
//         <br />

//         <div className="ppd-buttons">
//           {/* Apply now filters frontend from cached raw data */}
//           <button className="apply-btn" onClick={handleApplyFrontendOnly}>
//             Apply
//           </button>
//           <button className="clear-btn" onClick={handleClear}>
//             Clear
//           </button>
//         </div>
//       </div>

//       <div className="ppd-list-sections">
//         {/* CLIENT */}
//         <div className="ppd-list-item">
//           <div
//             className="ppd-list-header"
//             onClick={() => toggleSection("client")}
//           >
//             <span>Client → Status</span>
//             <span>{expanded.client ? "▲" : "▼"}</span>
//           </div>

//           {expanded.client && (
//             <div className="ppd-list-content">
//               {dashboardData.client.length ? (
//                 <table className="ppd-table">
//                   <thead>
//                     <tr>
//                       <th>Client</th>
//                       {dashboardData.statuses.map((s, i) => (
//                         <th key={i}>{s}</th>
//                       ))}
//                       <th>Total</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {dashboardData.client.map((row, i) => (
//                       <tr key={i}>
//                         <td>{row.name}</td>
//                         {dashboardData.statuses.map((s, j) => (
//                           <td key={j}>
//                             <span
//                               style={{ color: "#007bff", cursor: "pointer" }}
//                               onClick={() =>
//                                 handlePivotClick("client", row.name, s)
//                               }
//                             >
//                               {row.counts[s]}
//                             </span>
//                           </td>
//                         ))}
//                         <td>{row.total}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               ) : (
//                 <p className="no-data">No Data</p>
//               )}
//             </div>
//           )}
//         </div>

//         {/* PROJECT */}
//         <div className="ppd-list-item">
//           <div
//             className="ppd-list-header"
//             onClick={() => toggleSection("project")}
//           >
//             <span>Project → Status</span>
//             <span>{expanded.project ? "▲" : "▼"}</span>
//           </div>

//           {expanded.project && (
//             <div className="ppd-list-content">
//               {dashboardData.project.length ? (
//                 <table className="ppd-table">
//                   <thead>
//                     <tr>
//                       <th>Project</th>
//                       {dashboardData.statuses.map((s, i) => (
//                         <th key={i}>{s}</th>
//                       ))}
//                       <th>Total</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {dashboardData.project.map((row, i) => (
//                       <tr key={i}>
//                         <td>{row.name}</td>
//                         {dashboardData.statuses.map((s, j) => (
//                           <td key={j}>
//                             <span
//                               style={{ color: "#007bff", cursor: "pointer" }}
//                               onClick={() =>
//                                 handlePivotClick("project", row.name, s)
//                               }
//                             >
//                               {row.counts[s]}
//                             </span>
//                           </td>
//                         ))}
//                         <td>{row.total}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               ) : (
//                 <p className="no-data">No Data</p>
//               )}
//             </div>
//           )}
//         </div>

//         {/* PRIORITY */}
//         <div className="ppd-list-item">
//           <div
//             className="ppd-list-header"
//             onClick={() => toggleSection("priority")}
//           >
//             <span>Priority → Status</span>
//             <span>{expanded.priority ? "▲" : "▼"}</span>
//           </div>

//           {expanded.priority && (
//             <div className="ppd-list-content">
//               {dashboardData.priority.length ? (
//                 <table className="ppd-table">
//                   <thead>
//                     <tr>
//                       <th>Priority</th>
//                       {dashboardData.statuses.map((s, i) => (
//                         <th key={i}>{s}</th>
//                       ))}
//                       <th>Total</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {dashboardData.priority.map((row, i) => (
//                       <tr key={i}>
//                         <td>{row.name}</td>
//                         {dashboardData.statuses.map((s, j) => (
//                           <td key={j}>
//                             <span
//                               style={{ color: "#007bff", cursor: "pointer" }}
//                               onClick={() =>
//                                 handlePivotClick("priority", row.name, s)
//                               }
//                             >
//                               {row.counts[s]}
//                             </span>
//                           </td>
//                         ))}
//                         <td>{row.total}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               ) : (
//                 <p className="no-data">No Data</p>
//               )}
//             </div>
//           )}
//         </div>

//         {/* RESOURCE */}
//         <div className="ppd-list-item">
//           <div
//             className="ppd-list-header"
//             onClick={() => toggleSection("resource")}
//           >
//             <span>Resource → Status</span>
//             <span>{expanded.resource ? "▲" : "▼"}</span>
//           </div>

//           {expanded.resource && (
//             <div className="ppd-list-content">
//               {dashboardData.resource.length ? (
//                 <table className="ppd-table">
//                   <thead>
//                     <tr>
//                       <th>Resource</th>
//                       {dashboardData.statuses.map((s, i) => (
//                         <th key={i}>{s}</th>
//                       ))}
//                       <th>Total</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {dashboardData.resource.map((row, i) => (
//                       <tr key={i}>
//                         <td>{row.name}</td>
//                         {dashboardData.statuses.map((s, j) => (
//                           <td key={j}>
//                             <span
//                               style={{ color: "#007bff", cursor: "pointer" }}
//                               onClick={() =>
//                                 handlePivotClick("resource", row.name, s)
//                               }
//                             >
//                               {row.counts[s]}
//                             </span>
//                           </td>
//                         ))}
//                         <td>{row.total}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               ) : (
//                 <p className="no-data">No Data</p>
//               )}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProjectProgressDashboard;
